import { Box, Input, Text, type InputProps } from '@chakra-ui/react';
import { forwardRef, useId } from 'react';

type FloatingLabelInputProps = Omit<InputProps, 'placeholder'> & {
  label: string;
};

const FloatingLabelInput = forwardRef<HTMLInputElement, FloatingLabelInputProps>(
  ({ label, id, width = '100%', ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
      <Box
        position={'relative'}
        width={width}
        sx={{
          '& input:placeholder-shown:not([aria-invalid="true"])': { borderColor: 'transparent' },
          '& input:placeholder-shown + label': {
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
          },
          '& input:not(:placeholder-shown)': { paddingTop: '24px', paddingBottom: '8px' },
          '& input:focus-visible': { borderColor: 'text.mediumBlue', boxShadow: '0 0 0 1px #004179' },
          '& input:disabled + label': { opacity: 0.4 },
        }}
      >
        <Input
          ref={ref}
          id={inputId}
          variant={'unstyled'}
          height={'54px'}
          width={'100%'}
          background={'white'}
          border={'1px solid'}
          borderColor={'text.mediumBlue'}
          borderRadius={'8px'}
          paddingX={'20px'}
          fontSize={'16px'}
          fontWeight={600}
          color={'text.mediumBlue'}
          boxShadow={'0px 2px 4px 0px #0041790D'}
          _invalid={{ borderColor: 'extra.red', boxShadow: '0 0 0 1px var(--chakra-colors-extra-red)' }}
          {...props}
          placeholder={' '}
        />
        <Text
          as={'label'}
          htmlFor={inputId}
          position={'absolute'}
          left={'21px'}
          top={'10px'}
          color={'background.blueGray'}
          fontSize={'11px'}
          lineHeight={'16px'}
          fontWeight={800}
          textTransform={'uppercase'}
          cursor={'text'}
          pointerEvents={'none'}
        >
          {label}
        </Text>
      </Box>
    );
  },
);

FloatingLabelInput.displayName = 'FloatingLabelInput';

export default FloatingLabelInput;
